<template>
  <div class="instructor-card">
    <div class="instructor-header">
      <div class="instructor-image-container">
        <img :src="instructor.img_url" :alt="instructor.first_name + ' ' + instructor.last_name" class="instructor-image">
        <div class="instructor-rating">
          <i class="fas fa-star"></i>
          <span>{{ instructor.rating }}</span>
        </div>
      </div>
      <div class="instructor-info">
        <h2 class="instructor-name">{{ instructor.first_name }} {{ instructor.last_name }}</h2>
        <div class="instructor-expertise">
          <i class="fas fa-tag"></i>
          <span>{{ instructor.expertise }}</span>
        </div>
        <div class="instructor-stats">
          <span class="courses-count">
            <i class="fas fa-book"></i>
            {{ instructor.course_count }} courses
          </span>
          <span class="reviews-count">
            <i class="fas fa-comment"></i>
            {{ instructor.total_reviews }} reviews
          </span>
        </div>
      </div>
    </div>

    <div class="instructor-bio" v-html="instructor.bio"></div>

    <div v-if="instructor.courses && instructor.courses.length > 0" class="courses-section">
      <h3 class="courses-title">Featured Courses</h3>
      <div class="courses-grid">
        <div v-for="course in instructor.courses.slice(0, 2)" :key="course.id" class="course-card">
          <h4 class="course-title">{{ course.title }}</h4>
          <p class="course-description" v-html="course.description"></p>
          <div class="course-footer">
            <span v-if="course.price" class="course-price">${{ course.price }}</span>
            <span v-else class="course-price free">Free</span>
            <span v-if="course.is_premium" class="premium-badge">Premium</span>
          </div>
        </div>
      </div>
    </div>
    
    <div class="action-buttons">
      <button @click="$emit('view-profile', instructor.id)" class="view-details-btn">
        <i class="fas fa-eye"></i> View Profile
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'InstructorCard',
  props: {
    instructor: {
      type: Object,
      required: true
    }
  },
  emits: ['view-profile']
};
</script>

<style scoped>
.instructor-card {
  background: var(--bg-white);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-lg);
  overflow: hidden;
  transition: var(--transition);
  border: none;
  display: flex;
  flex-direction: column;
  position: relative;
}

.instructor-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 8px;
  background: linear-gradient(90deg, var(--primary-color), var(--secondary-color));
}

.instructor-card:hover {
  transform: translateY(-8px);
  box-shadow: var(--shadow-xl);
}

.instructor-header {
  padding: 1.2rem;
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
  text-align: center;
  border-bottom: 1px solid var(--border-color);
  position: relative;
}

.instructor-image-container {
  position: relative;
  margin-bottom: 1rem;
}

.instructor-image {
  width: 110px;
  height: 110px;
  border-radius: 50%;
  object-fit: cover;
  border: 4px solid var(--bg-white);
  box-shadow: var(--shadow-lg);
  margin: 0 auto;
  display: block;
  transition: var(--transition);
}

.instructor-card:hover .instructor-image {
  transform: scale(1.05);
  border-color: var(--primary-light);
}

.instructor-rating {
  position: absolute;
  bottom: 0;
  right: 50%;
  transform: translateX(50%);
  background: linear-gradient(135deg, var(--primary-color), var(--secondary-color));
  color: var(--bg-white);
  padding: 0.75rem 1.25rem;
  border-radius: 50px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: var(--shadow-md);
}

.instructor-info {
  margin-top: 0.7rem;
}

.instructor-name {
  font-size: 1.2rem;
  color: var(--text-dark);
  margin-bottom: 0.5rem;
  font-weight: 800;
}

.instructor-expertise {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  color: var(--text-medium);
  font-size: 0.95rem;
  margin-bottom: 0.7rem;
  padding: 0.4rem 1rem;
  background: var(--primary-light);
  border-radius: 50px;
  font-weight: 600;
}

.instructor-stats {
  display: flex;
  justify-content: center;
  gap: 1rem;
  font-size: 0.95rem;
  color: var(--text-medium);
}

.instructor-stats i {
  margin-right: 0.5rem;
  color: var(--primary-color);
}

.instructor-bio {
  padding: 1rem;
  color: var(--text-medium);
  font-size: 0.95rem;
  line-height: 1.8;
  border-bottom: 1px solid var(--border-color);
}

.courses-section {
  padding: 1rem;
  background: var(--bg-white);
}

.courses-title {
  font-size: 1.1rem;
  color: var(--text-dark);
  margin-bottom: 0.8rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 1rem;
}

.courses-title::before {
  content: '';
  display: block;
  width: 4px;
  height: 1.2rem;
  background: linear-gradient(135deg, var(--primary-color), var(--secondary-color));
  border-radius: var(--radius-lg);
}

.courses-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.7rem;
}

.course-card {
  background: var(--bg-light);
  border-radius: var(--radius-xl);
  padding: 0.8rem;
  border: 1px solid var(--border-color);
  transition: var(--transition);
}

.course-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-md);
  border-color: var(--primary-light);
}

.course-title {
  font-size: 1rem;
  color: var(--text-dark);
  margin-bottom: 0.5rem;
  font-weight: 700;
}

.course-description {
  font-size: 0.9rem;
  color: var(--text-medium);
  margin-bottom: 0.6rem;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.7;
}

.course-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.6rem;
  padding-top: 0.6rem;
  border-top: 1px solid var(--border-color);
}

.course-price {
  font-weight: 700;
  font-size: 1rem;
  color: var(--text-dark);
}

.course-price.free {
  color: #10b981;
}

.premium-badge {
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  color: #78350f;
  padding: 0.3rem 0.7rem;
  border-radius: 50px;
  font-size: 0.8rem;
  font-weight: 700;
  box-shadow: var(--shadow-sm);
}

.action-buttons {
  padding: 1rem;
  border-top: 1px solid var(--border-color);
  margin-top: auto;
}

.view-details-btn {
  width: 100%;
  padding: 0.7rem 1.2rem;
  background: linear-gradient(135deg, var(--primary-color), var(--secondary-color));
  color: var(--bg-white);
  border: none;
  border-radius: var(--radius-xl);
  font-weight: 700;
  cursor: pointer;
  transition: var(--transition);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  font-size: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.view-details-btn:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-lg);
  background: linear-gradient(135deg, var(--secondary-color), var(--primary-color));
}

@media (max-width: 768px) {
  .instructor-image {
    width: 140px;
    height: 140px;
  }
}

@media (max-width: 576px) {
  .instructor-image {
    width: 120px;
    height: 120px;
  }
  
  .instructor-name {
    font-size: 1.5rem;
  }
}
</style> 